import { clsx } from 'clsx';
import type { ComponentProps, ReactNode } from 'react';

export type ButtonVariant = 'primary' | 'success' | 'outline' | 'text';

export interface ButtonProps extends ComponentProps<'button'> {
  /** Visual variant of the button */
  variant?: ButtonVariant;
  /** Button content */
  children: ReactNode;
  /** Whether the button spans full width */
  fullWidth?: boolean;
  /** Loading state - shows spinner and disables interaction */
  loading?: boolean;
}

const variantStyles: Record<ButtonVariant, string> = {
  primary: 'bg-dorsia-black text-dorsia-white hover:opacity-90',
  success: 'bg-dorsia-green text-dorsia-white hover:bg-dorsia-green-dark',
  outline: 'bg-transparent border border-dorsia-black text-dorsia-black hover:bg-dorsia-black hover:text-dorsia-white',
  text: 'bg-transparent text-dorsia-black underline hover:opacity-70 px-0',
};

/**
 * Button - Atom component for interactive buttons
 *
 * Supports primary (black), success (green), outline, and text-only variants.
 * Includes loading state with spinner and full-width option.
 */
export function Button({
  variant = 'primary',
  children,
  fullWidth = false,
  loading = false,
  disabled,
  className,
  ...props
}: ButtonProps) {
  return (
    <button
      type="button"
      disabled={disabled || loading}
      className={clsx(
        'font-sailec inline-flex cursor-pointer items-center justify-center rounded-lg text-base font-medium transition-all',
        variant !== 'text' && 'h-12 px-6',
        fullWidth && 'w-full',
        (disabled || loading) && 'cursor-not-allowed opacity-50',
        variantStyles[variant],
        className
      )}
      {...props}
    >
      {loading && (
        <svg
          className="mr-2 h-4 w-4 animate-spin"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      )}
      {children}
    </button>
  );
}
